'use client';

import React from 'react';
import { cn } from '@/utils';
import { FEATURE_FLAGS } from '@/constants';
import { AudioSvg } from '@/components/Icons/AudioSvg';
import { AudioChannelMeter } from './AudioChannelMeter';

export interface AudioVisualizerProps {
  /** Whether the audio-only visualization should be visible. */
  visible: boolean;
  /** Analyser feeding the avatar (output) channel meter. */
  outputAnalyser: AnalyserNode | null;
  /** Analyser feeding the user (input) channel meter. */
  inputAnalyser: AnalyserNode | null;
  /** Display name for the avatar/output channel. */
  personaName?: string;
}

/**
 * Audio-only visualization shown in place of the avatar video. Renders either
 * the dual input/output channel meters (when ENABLE_AUDIO_IO_VISUALIZER is on)
 * or the legacy animated waveform.
 */
export const AudioVisualizer: React.FC<AudioVisualizerProps> = ({
  visible,
  outputAnalyser,
  inputAnalyser,
  personaName,
}) => (
  <div className="absolute inset-0 flex items-center justify-center">
    {FEATURE_FLAGS.ENABLE_AUDIO_IO_VISUALIZER ? (
      <div
        className={cn('mx-auto flex w-3/5 flex-col gap-3', {
          hidden: !visible,
        })}
      >
        <AudioChannelMeter
          analyser={outputAnalyser}
          barColorClassName="bg-accent-blue-500"
          label={personaName ?? 'Avatar'}
        />
        <AudioChannelMeter
          analyser={inputAnalyser}
          barColorClassName="bg-brand-primary"
          label="You"
        />
      </div>
    ) : (
      <AudioSvg
        animate={true}
        size="auto"
        className={cn('w-full max-w-md', {
          hidden: !visible,
        })}
      />
    )}
  </div>
);
